import { indicatorAutoLabel } from "@strat/format";
import type { ConditionNode, IndicatorDef, Operand, StrategySpec } from "@strat/format";

/**
 * A strategy, read aloud: its indicators, its entry and exit rules, its risk.
 * What a visitor needs to decide whether it is worth forking — without
 * opening the builder.
 */
/** The builder's own name for it, so "ST(10,3)" reads the same everywhere. */
function indicatorName(d: IndicatorDef, all?: readonly IndicatorDef[]): string {
  return d.label?.trim() || indicatorAutoLabel(d, all);
}

function ago(o: Operand): string {
  const n = o.kind === "const" ? 0 : (o.offset ?? 0);
  return n > 0 ? ` ${n} bar${n === 1 ? "" : "s"} ago` : "";
}

function operand(o: Operand, byId: Map<string, IndicatorDef>): string {
  if (o.kind === "const") return String(o.value);
  if (o.kind === "price") return `price ${o.field}${ago(o)}`;
  const d = byId.get(o.ref);
  const name = d ? indicatorName(d, [...byId.values()]) : o.ref;
  return `${o.output && o.output !== "value" ? `${name}.${o.output}` : name}${ago(o)}`;
}

const OPS: Record<string, string> = {
  gt: "is above",
  lt: "is below",
  crosses_above: "crosses above",
  crosses_below: "crosses below",
};

function Rule({ node, byId }: { node: ConditionNode; byId: Map<string, IndicatorDef> }) {
  if ("children" in node) {
    if (node.children.length === 0) return <span className="muted">(no rule)</span>;
    return (
      <ul className="rule-group">
        {node.children.map((c, i) => (
          <li key={i}>
            {i > 0 && <span className="rule-join">{node.op === "and" ? "and" : "or"}</span>}
            <Rule node={c} byId={byId} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <span className="rule">
      <code>{operand(node.left, byId)}</code> {OPS[node.op] ?? node.op}{" "}
      <code>{operand(node.right, byId)}</code>
    </span>
  );
}

export function SpecSummary({ spec }: { spec: StrategySpec }) {
  const byId = new Map(spec.indicators.map((d) => [d.id, d]));
  const r = spec.risk;
  const risk = [
    `${r.sizeMode === "pct_equity" ? `${r.sizeValue}% of equity` : `${r.sizeValue} ${r.sizeMode === "fixed_quote" ? "quote" : "units"}`} per trade`,
    r.stopLossPct ? `stop-loss ${r.stopLossPct}%` : "no stop-loss",
    r.takeProfitPct ? `take-profit ${r.takeProfitPct}%` : null,
    r.trailingStopPct ? `trailing stop ${r.trailingStopPct}%` : null,
    (r.leverage ?? 1) > 1 ? `${r.leverage}× leverage` : null,
    `fees ${r.feePct ?? 0}%`,
  ].filter(Boolean);
  return (
    <dl className="spec-summary">
      <dt>Direction</dt>
      <dd>{spec.direction === "short" ? "Short" : "Long"}</dd>
      <dt>Indicators</dt>
      <dd>
        {spec.indicators.length ? (
          spec.indicators.map((d) => (
            <code
              key={d.id}
              title={d.kind === "formula" ? `= ${indicatorAutoLabel(d, spec.indicators)}` : undefined}
            >
              {indicatorName(d, spec.indicators)}
            </code>
          ))
        ) : (
          <span className="muted">none — price only</span>
        )}
      </dd>
      <dt>Enter when</dt>
      <dd>
        <Rule node={spec.entry} byId={byId} />
      </dd>
      <dt>Exit when</dt>
      <dd>
        <Rule node={spec.exit} byId={byId} />
      </dd>
      <dt>Risk</dt>
      <dd>{risk.join(" · ")}</dd>
    </dl>
  );
}
